import { useState, useEffect } from 'react';
import axios from 'axios';

const base_url = process.env.REACT_APP_BASE_URL;

// Set REACT_APP_GLUCOSE_TARGETS_API=false in .env to skip API while backend is not ready
const API_ENABLED = process.env.REACT_APP_GLUCOSE_TARGETS_API !== 'false';

// Default target range: 70–180 mg/dL (standard TIR range)
const DEFAULT_LOWER = 70;
const DEFAULT_UPPER = 180;

/**
 * Fetches user's custom glucose targets from GET /api/settings/get-glucose-data/<uuid>
 * Falls back to checking localStorage for ranges from other API calls.
 * Returns { lower_range, upper_range } in mg/dL.
 * Backend always returns mg/dL; defaults to 70–180 mg/dL when unavailable.
 */
export function useGlucoseTargets(uuid) {
  const [targets, setTargets] = useState({
    lower_range: DEFAULT_LOWER,
    upper_range: DEFAULT_UPPER,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!uuid) {
      setTargets({ lower_range: DEFAULT_LOWER, upper_range: DEFAULT_UPPER });
      setLoading(false);
      return;
    }

    // First, check if we have ranges stored in localStorage from other API calls
    const storedRanges = localStorage.getItem('glucose_ranges');
    if (storedRanges) {
      try {
        const parsed = JSON.parse(storedRanges);
        if (parsed.uuid === uuid && parsed.lower_range && parsed.upper_range) {
          console.log('[useGlucoseTargets] Using cached ranges from localStorage:', parsed);
          setTargets({
            lower_range: Math.round(Number(parsed.lower_range)),
            upper_range: Math.round(Number(parsed.upper_range)),
          });
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn('[useGlucoseTargets] Failed to parse stored ranges');
      }
    }

    if (!API_ENABLED) {
      setTargets({ lower_range: DEFAULT_LOWER, upper_range: DEFAULT_UPPER });
      setLoading(false);
      return;
    }

    let cancelled = false;
    const user = localStorage.getItem('user')?.replace(/['"]+/g, '');
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);
    
    console.log('[useGlucoseTargets] Fetching for UUID:', uuid);
    
    axios
      .get(`${base_url}/settings/get-glucose-data/${uuid}`, {
        headers: { Authorization: `Bearer ${user}` }
      })
      .then((res) => {
        if (cancelled) return;
        console.log('[useGlucoseTargets] Response:', res.data);
        if (res.data?.status === 200 && Array.isArray(res.data.response) && res.data.response.length > 0) {
          const row = res.data.response[0];
          const lower = Math.round(Number(row.lower_range)) || DEFAULT_LOWER;
          const upper = Math.round(Number(row.upper_range)) || DEFAULT_UPPER;
          console.log('[useGlucoseTargets] Using custom ranges (mg/dL):', { lower, upper });
          setTargets({ lower_range: lower, upper_range: upper });
          localStorage.setItem('glucose_ranges', JSON.stringify({ uuid, lower_range: lower, upper_range: upper }));
        } else {
          console.log('[useGlucoseTargets] No custom data, using defaults');
        }
      })
      .catch((err) => {
        console.error('[useGlucoseTargets] Error:', err.response?.status, err.response?.data || err.message);
        if (!cancelled) {
          setTargets({ lower_range: DEFAULT_LOWER, upper_range: DEFAULT_UPPER });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [uuid]);

  return { ...targets, loading };
}
